import { type ValidationError } from '@wix/form-validator';

import { INPUT_FIELD_TYPES, type InputFieldType } from '../constants';
import { useConfig } from '../contexts/config-context';
import { ForcedState, useForcedState } from '../contexts/forced-state-context';
import { useTranslation } from '../contexts/translation-context';
import { getDateValidationErrorSuffix } from '../ui/date-field/date-field-utils';
import { getDateTimeValidationErrorSuffix } from '../ui/date-time-field/date-time-field-utils';
import { type PossibleValues, type Properties } from '../ui/form/types';

import { type CustomErrors, type FieldError } from './error-types';
import { getTranslatedError } from './get-translated-error';

const getErrorSuffix = ({
  fieldType,
  error,
  properties,
}: {
  fieldType: InputFieldType;
  error?: ValidationError;
  properties?: Properties<PossibleValues, string>;
}) => {
  switch (fieldType) {
    case INPUT_FIELD_TYPES.DATE_TIME_INPUT:
      return getDateTimeValidationErrorSuffix(error);
    case INPUT_FIELD_TYPES.CONTACTS_BIRTHDATE:
    case INPUT_FIELD_TYPES.DATE_INPUT:
      return getDateValidationErrorSuffix(error);
    case INPUT_FIELD_TYPES.TEXT_INPUT:
    case INPUT_FIELD_TYPES.CHALLENGES_SHORT_TEXT:
    case INPUT_FIELD_TYPES.QUIZ_TEXT_INPUT:
    case INPUT_FIELD_TYPES.DEXT_TEXT_INPUT:
    case INPUT_FIELD_TYPES.DEXT_EMAIL:
    case INPUT_FIELD_TYPES.CONTACTS_TAX_ID:
    case INPUT_FIELD_TYPES.CONTACTS_POSITION:
    case INPUT_FIELD_TYPES.CONTACTS_LAST_NAME:
    case INPUT_FIELD_TYPES.CONTACTS_FIRST_NAME:
    case INPUT_FIELD_TYPES.CONTACTS_EMAIL:
    case INPUT_FIELD_TYPES.CONTACTS_COMPANY:
    case INPUT_FIELD_TYPES.CONTACTS_ADDRESS:
    case INPUT_FIELD_TYPES.BOOKINGS_LAST_NAME:
    case INPUT_FIELD_TYPES.BOOKINGS_FIRST_NAME:
    case INPUT_FIELD_TYPES.BOOKINGS_EMAIL:
    case INPUT_FIELD_TYPES.ECOM_PHONE:
    case INPUT_FIELD_TYPES.ECOM_EMAIL:
    case INPUT_FIELD_TYPES.ECOM_COMPANY_NAME:
    case INPUT_FIELD_TYPES.ECOM_ADDITIONAL_INFO:
    case INPUT_FIELD_TYPES.FULL_NAME_LAST_NAME:
    case INPUT_FIELD_TYPES.FULL_NAME_FIRST_NAME:
    case INPUT_FIELD_TYPES.MLA_STREET_NAME:
    case INPUT_FIELD_TYPES.MLA_ADDRESS_LINE:
    case INPUT_FIELD_TYPES.MLA_STREET_NUMBER:
    case INPUT_FIELD_TYPES.MLA_POSTAL_CODE:
    case INPUT_FIELD_TYPES.MLA_CITY:
    case INPUT_FIELD_TYPES.MLA_APARTMENT:
    case INPUT_FIELD_TYPES.MLA_ADDRESS_LINE_2:
    case INPUT_FIELD_TYPES.PASSWORD:
    case INPUT_FIELD_TYPES.RATING_INPUT:
    case INPUT_FIELD_TYPES.DEXT_RATING_INPUT:
    case INPUT_FIELD_TYPES.MLA_COUNTRY:
    case INPUT_FIELD_TYPES.MLA_SUBDIVISION:
      if (properties && 'countryCode' in properties) {
        const countryCode = properties.countryCode;
        return typeof countryCode === 'string'
          ? countryCode.toLocaleLowerCase()
          : undefined;
      }

      return undefined;
    case INPUT_FIELD_TYPES.VAT_ID:
      if (properties && 'country' in properties) {
        const country = properties.country;
        return typeof country === 'string'
          ? country.toLocaleLowerCase()
          : undefined;
      }

      return undefined;
    case INPUT_FIELD_TYPES.SIGNATURE:
      if (properties && 'uploadEnabled' in properties) {
        return properties.uploadEnabled ? 'with-upload' : undefined;
      }

      return undefined;
    default:
      return undefined;
  }
};

interface UseErrorParams {
  error: FieldError | undefined;
  customErrors?: CustomErrors;
  fieldType?: string;
  properties?: Properties<PossibleValues, string>;
  validationError?: ValidationError;
}

export const useError = ({
  error,
  customErrors,
  fieldType,
  properties,
  validationError,
}: UseErrorParams) => {
  const { currency } = useConfig();
  const { t, i18n } = useTranslation();
  const forcedState = useForcedState();

  if (forcedState.includes(ForcedState.INPUT_FIELDS_ERROR)) {
    return {
      hasError: true,
      errorMessage: t('input.error.message.required-error-forced'),
    };
  }

  if (!error) {
    return { hasError: false };
  }

  const customErrorMessage =
    (error?.useCustomErrorMessage && error?.errorMessage) ||
    customErrors?.[error.errorType];

  const suffix = fieldType
    ? getErrorSuffix({
        fieldType: fieldType as InputFieldType,
        error: validationError,
        properties,
      })
    : undefined;

  const errorMessage =
    customErrorMessage ??
    getTranslatedError({
      translation: { i18n, t },
      error,
      fieldType,
      suffix,
      currency: currency?.sign,
    });

  return { hasError: true, errorMessage };
};
